// HabitTab — daily habit tracker ported from Apps/Yawmi. Storage: aweni_yawmi_habits.
import type { AweniContext } from '../../types';

interface Habit {
  id: string; name: string; icon: string; category: string;
  frequency: 'daily' | 'weekdays' | 'weekly' | 'custom'; days?: number[];
  targetCount: number; unit: string;
  streakCurrent: number; streakBest: number; createdAt: string;
}
interface HabitLog { habitId: string; date: string; count: number; }

const HABITS_KEY = 'aweni_yawmi_habits';
const LOGS_KEY = 'aweni_yawmi_habit_logs';

const PRESETS: Habit[] = [
  { id: 'p_fajr', name: 'yawmi_habits_preset_fajr_name', icon: '🕌', category: 'worship', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_quran', name: 'yawmi_habits_preset_quran_pages_name', icon: '📖', category: 'knowledge', frequency: 'daily', targetCount: 3, unit: 'pages', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_dhikr_m', name: 'yawmi_habits_preset_dhikr_morning_name', icon: '🌅', category: 'worship', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_dhikr_e', name: 'yawmi_habits_preset_dhikr_evening_name', icon: '🌙', category: 'worship', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_walk', name: 'yawmi_habits_preset_walk_name', icon: '🚶', category: 'health', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_water', name: 'yawmi_habits_preset_water_name', icon: '💧', category: 'health', frequency: 'daily', targetCount: 8, unit: 'glasses', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_sleep', name: 'yawmi_habits_preset_sleep_early_name', icon: '😴', category: 'health', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_book', name: 'yawmi_habits_preset_read_book_name', icon: '📚', category: 'knowledge', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_family', name: 'yawmi_habits_preset_family_call_name', icon: '📞', category: 'social', frequency: 'weekly', days: [0, 5, 6], targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_journal', name: 'yawmi_habits_preset_journal_name', icon: '✏️', category: 'personal', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
];

export class HabitTab {
  private el: HTMLElement;
  private habits: Habit[] = [];
  private logs: HabitLog[] = [];
  private view: 'tracker' | 'browse' = 'tracker';
  private filterCat = 'all';

  constructor(container: HTMLElement) { this.el = container; }

  mount(ctx: AweniContext): void {
    try { this.habits = JSON.parse(localStorage.getItem(HABITS_KEY) || '[]'); } catch { this.habits = []; }
    try { this.logs = JSON.parse(localStorage.getItem(LOGS_KEY) || '[]'); } catch { this.logs = []; }
    this.render(ctx);
  }

  unmount(): void { this.el.innerHTML = ''; }

  private tr(ctx: AweniContext, k: string, fb: string): string { return ctx.i18n.t(k) || fb; }
  private today(): string { return new Date().toISOString().slice(0, 10); }
  private todayCount(id: string): number { return this.logs.find(l => l.habitId === id && l.date === this.today())?.count ?? 0; }
  private isDone(h: Habit): boolean { return this.todayCount(h.id) >= h.targetCount; }
  private showToday(h: Habit): boolean {
    const dow = new Date().getDay();
    return h.frequency === 'daily' ? true : h.frequency === 'weekdays' ? dow >= 1 && dow <= 5 : (h.days ?? []).includes(dow);
  }

  private save(): void {
    localStorage.setItem(HABITS_KEY, JSON.stringify(this.habits));
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 120);
    this.logs = this.logs.filter(l => l.date >= cutoff.toISOString().slice(0, 10));
    localStorage.setItem(LOGS_KEY, JSON.stringify(this.logs));
  }

  private increment(id: string): void {
    const existing = this.logs.find(l => l.habitId === id && l.date === this.today());
    if (existing) { existing.count++; } else { this.logs.push({ habitId: id, date: this.today(), count: 1 }); }
    this.save();
  }

  private delete(id: string): void {
    this.habits = this.habits.filter(h => h.id !== id);
    this.logs = this.logs.filter(l => l.habitId !== id);
    this.save();
  }

  private addPreset(preset: Habit): void {
    if (this.habits.some(h => h.id === preset.id)) return;
    this.habits.push({ ...preset, createdAt: new Date().toISOString() });
    this.save();
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    const todayHabits = this.habits.filter(h => this.showToday(h));
    const done = todayHabits.filter(h => this.isDone(h)).length;

    if (this.view === 'browse') { this.renderBrowse(ctx); return; }

    const habitRows = todayHabits.map(h => {
      const count = this.todayCount(h.id);
      const pct = Math.min(100, Math.round((count / h.targetCount) * 100));
      const done_ = count >= h.targetCount;
      return `
        <div class="ht-row" data-id="${h.id}" style="display:flex;align-items:center;gap:12px;padding:12px;
          background:var(--ln-bg-card);border-radius:var(--ln-radius-md);margin-bottom:8px;
          opacity:${done_ ? '.65' : '1'}">
          <span style="font-size:1.4rem">${h.icon}</span>
          <div style="flex:1;min-width:0">
            <div style="font-weight:600;font-size:.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              ${t(h.name, h.name)}
            </div>
            <div style="height:4px;background:var(--ln-border,rgba(0,0,0,.1));border-radius:2px;margin-top:4px">
              <div style="height:4px;width:${pct}%;background:var(--ln-accent);border-radius:2px;transition:width .3s"></div>
            </div>
            <div style="font-size:.75rem;color:var(--ln-text-secondary);margin-top:2px">${count}/${h.targetCount}${h.unit ? ' ' + h.unit : ''}</div>
          </div>
          ${done_ ? `<span style="color:var(--ln-accent);font-size:1.2rem">✓</span>` : `
            <button class="ht-inc" data-id="${h.id}" style="padding:6px 10px;background:var(--ln-accent);color:var(--ln-accent-text);
              border:none;border-radius:var(--ln-radius-sm);cursor:pointer;font-weight:700">+1</button>
          `}
          <button class="ht-del" data-id="${h.id}" style="padding:4px 8px;background:transparent;border:1px solid var(--ln-border);
            border-radius:var(--ln-radius-sm);cursor:pointer;font-size:.75rem;color:var(--ln-text-secondary)">×</button>
        </div>`;
    }).join('');

    this.el.innerHTML = `
      <div style="padding:0 0 16px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <div>
            <div style="font-size:1.1rem;font-weight:700">${done}/${todayHabits.length} ${t('yawmi_habits_today', 'habits today')}</div>
            <div style="height:6px;background:var(--ln-border);border-radius:3px;margin-top:4px;width:160px">
              <div style="height:6px;background:var(--ln-accent);border-radius:3px;width:${todayHabits.length ? Math.round(done/todayHabits.length*100) : 0}%"></div>
            </div>
          </div>
          <button id="ht-browse-btn" style="padding:8px 14px;background:var(--ln-accent);color:var(--ln-accent-text);
            border:none;border-radius:var(--ln-radius-full);cursor:pointer;font-size:.85rem;font-weight:700">
            + ${t('yawmi_habits_add', 'Add Habit')}
          </button>
        </div>
        ${habitRows || `<p style="color:var(--ln-text-secondary);text-align:center;padding:32px 0">${t('yawmi_habits_empty', 'No habits for today.')}</p>`}
      </div>`;

    this.el.querySelectorAll<HTMLButtonElement>('.ht-inc').forEach(btn => {
      btn.addEventListener('click', () => {
        this.increment(btn.dataset.id!);
        this.render(ctx);
      });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.ht-del').forEach(btn => {
      btn.addEventListener('click', () => {
        if (confirm(t('yawmi_habits_delete_confirm', 'Delete this habit?'))) {
          this.delete(btn.dataset.id!);
          this.render(ctx);
        }
      });
    });
    this.el.querySelector('#ht-browse-btn')?.addEventListener('click', () => {
      this.view = 'browse';
      this.render(ctx);
    });
  }

  private renderBrowse(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    const cats = ['all', 'worship', 'health', 'knowledge', 'social', 'personal'];
    const filtered = this.filterCat === 'all' ? PRESETS : PRESETS.filter(p => p.category === this.filterCat);

    this.el.innerHTML = `
      <div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <strong>${t('yawmi_habits_browse_title', 'Add Habits')}</strong>
          <button id="ht-back" style="padding:6px 12px;background:transparent;border:1px solid var(--ln-border);
            border-radius:var(--ln-radius-full);cursor:pointer;font-size:.85rem">${t('yawmi_habits_back', 'Back')}</button>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px">
          ${cats.map(c => `<button class="ht-cat-btn" data-cat="${c}" style="padding:4px 10px;border-radius:var(--ln-radius-full);
            border:1px solid var(--ln-border);background:${c === this.filterCat ? 'var(--ln-accent)' : 'var(--ln-bg-card)'};
            color:${c === this.filterCat ? 'var(--ln-accent-text)' : 'inherit'};cursor:pointer;font-size:.8rem">
            ${t('yawmi_habits_cat_' + c, c)}</button>`).join('')}
        </div>
        ${filtered.map(p => {
          const added = this.habits.some(h => h.id === p.id);
          return `<div style="display:flex;align-items:center;gap:10px;padding:10px;background:var(--ln-bg-card);
            border-radius:var(--ln-radius-md);margin-bottom:6px">
            <span style="font-size:1.3rem">${p.icon}</span>
            <span style="flex:1;font-size:.9rem">${t(p.name, p.name)}</span>
            <button class="ht-add-preset" data-id="${p.id}" ${added ? 'disabled' : ''} style="padding:5px 10px;
              background:${added ? 'var(--ln-border)' : 'var(--ln-accent)'};color:${added ? 'var(--ln-text-secondary)' : 'var(--ln-accent-text)'};
              border:none;border-radius:var(--ln-radius-sm);cursor:${added ? 'default' : 'pointer'};font-size:.8rem">
              ${added ? t('yawmi_habits_already_added', 'Added') : '+ ' + t('yawmi_habits_added', 'Add')}</button>
          </div>`;
        }).join('')}
      </div>`;

    this.el.querySelector('#ht-back')?.addEventListener('click', () => { this.view = 'tracker'; this.render(ctx); });
    this.el.querySelectorAll<HTMLButtonElement>('.ht-cat-btn').forEach(btn => {
      btn.addEventListener('click', () => { this.filterCat = btn.dataset.cat!; this.renderBrowse(ctx); });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.ht-add-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        const preset = PRESETS.find(p => p.id === btn.dataset.id);
        if (preset) { this.addPreset(preset); this.renderBrowse(ctx); }
      });
    });
  }
}
